/**
 * 拼图板
 * 管理所有碎片的集合，处理游戏逻辑
 */
import { Vector2, HexCoord, GameConfig, PieceState, LayoutConfig, EdgeDirection } from '../types';
import { PuzzlePiece } from './PuzzlePiece';
import { SnapSystem, SnapResult } from './SnapSystem';
import { ImageSlicer, SlicedPiece } from './ImageSlicer';
import { SmartLayoutManager } from './SmartLayoutManager';
import { GroupManager } from './GroupManager';
import { PieceGroup } from './PieceGroup';
import { Random } from '../core/Random';
import { HexMath } from '../core/HexMath';
import { SpatialIndex } from '../core/SpatialIndex';

export interface BoardEvents {
    onPieceSnapped?: (piece: PuzzlePiece, result: SnapResult) => void;
    onPiecePlaced?: (piece: PuzzlePiece) => void;
    onGroupPlaced?: (group: PieceGroup) => void;
    onGroupCreated?: (group: PieceGroup, piece1: PuzzlePiece, piece2: PuzzlePiece, direction: EdgeDirection) => void;
    onGroupMerged?: (survivingGroup: PieceGroup, mergedGroup: PieceGroup) => void;
    onPieceJoinedGroup?: (piece: PuzzlePiece, group: PieceGroup, targetPiece: PuzzlePiece, direction: EdgeDirection) => void;
    onPieceUnplaced?: (piece: PuzzlePiece) => void;
    onGameComplete?: () => void;
    onPieceSelected?: (piece: PuzzlePiece | null) => void;
}

export class PuzzleBoard {
    private pieces: Map<string, PuzzlePiece> = new Map();
    private slicedPieces: Map<string, SlicedPiece> = new Map();
    private config: GameConfig;
    private imageSlicer: ImageSlicer;
    private snapSystem: SnapSystem;
    private groupManager: GroupManager;
    private random: Random;
    private events: BoardEvents = {};

    // 智能布局管理器
    private smartLayoutManager: SmartLayoutManager;

    // 空间索引（用于加速吸附检测）
    private spatialIndex: SpatialIndex;

    // 游戏状态
    private _selectedPiece: PuzzlePiece | null = null;
    private _isComplete: boolean = false;

    constructor(config: GameConfig, layoutConfig?: Partial<LayoutConfig>) {
        this.config = config;
        this.imageSlicer = new ImageSlicer(config);
        // 吸附触发容差：固定 8 像素，仅用于判定触发时机
        // 触发后碎片会被移到底板 correctPosition 的精确位置
        this.snapSystem = new SnapSystem({ snapDistance: 12 });
        this.groupManager = new GroupManager();
        this.random = new Random(config.seed);
        this.smartLayoutManager = new SmartLayoutManager(layoutConfig, config.seed);
        this.spatialIndex = new SpatialIndex(200); // 网格大小 200 像素

        // 设置组管理器事件
        this.groupManager.setEvents({
            onGroupPlaced: (group) => {
                this.events.onGroupPlaced?.(group);
                this.checkGameComplete();
            }
        });
    }

    /**
     * 设置事件监听器
     */
    setEvents(events: BoardEvents): void {
        this.events = { ...this.events, ...events };
    }

    /**
     * 初始化拼图板
     */
    async initialize(imageSource: string | File): Promise<void> {
        // 加载并处理图片
        const img = await this.imageSlicer.processImage(imageSource);
        const resizedCanvas = this.imageSlicer.resizeImage(img);

        // 切割图片
        const slicedPieces = await this.imageSlicer.sliceImageAsync(resizedCanvas);

        // 创建碎片对象
        this.pieces.clear();
        this.slicedPieces.clear();

        for (const sliced of slicedPieces) {
            const pieceId = `piece_${sliced.coord.u}_${sliced.coord.v}`;

            // 创建碎片
            const piece = new PuzzlePiece(
                sliced.coord,
                sliced.center,
                sliced.pathData,
                sliced.edgeTypes
            );

            this.pieces.set(pieceId, piece);
            this.slicedPieces.set(pieceId, sliced);

            // 注册到空间索引
            this.spatialIndex.updatePiece(piece);
        }

        this._isComplete = false;
    }

    /**
     * 获取所有碎片
     */
    getAllPieces(): PuzzlePiece[] {
        return Array.from(this.pieces.values());
    }

    /**
     * 获取所有组
     */
    getGroups(): PieceGroup[] {
        return this.groupManager.getAllGroups();
    }

    /**
     * 获取碎片的纹理数据
     */
    getSlicedPiece(pieceId: string): SlicedPiece | undefined {
        return this.slicedPieces.get(pieceId);
    }

    /**
     * 根据 ID 获取碎片
     */
    getPiece(pieceId: string): PuzzlePiece | undefined {
        return this.pieces.get(pieceId);
    }

    /**
     * 根据坐标获取碎片
     */
    getPieceByCoord(coord: HexCoord): PuzzlePiece | undefined {
        const id = `piece_${coord.u}_${coord.v}`;
        return this.pieces.get(id);
    }

    /**
     * 根据坐标键获取碎片（格式：u_v）
     */
    getPieceByCoordKey(coordKey: string): PuzzlePiece | undefined {
        const id = `piece_${coordKey}`;
        return this.pieces.get(id);
    }

    /**
     * 获取当前选中的碎片
     */
    get selectedPiece(): PuzzlePiece | null {
        return this._selectedPiece;
    }

    /**
     * 选中碎片
     */
    selectPiece(piece: PuzzlePiece | null): void {
        this._selectedPiece = piece;
        this.events.onPieceSelected?.(piece);
    }

    /**
     * 根据屏幕位置查找碎片
     */
    findPieceAtPosition(position: Vector2): PuzzlePiece | null {
        // 计算动态碎片大小
        // 碎片尺寸约为 imageSize / (rings * 2 + 1)
        const pieceSize = this.config.imageSize / (this.config.rings * 2 + 1);
        // 适当扩大判定范围，方便点击
        const hitTestSize = Math.max(pieceSize * 0.8, 50);

        // 从上到下遍历（后绘制的在上面）
        const pieces = this.getAllPieces().reverse();

        for (const piece of pieces) {
            if (piece.isInPocket) continue;
            if (piece.containsPoint(position, hitTestSize)) {
                return piece;
            }
        }

        return null;
    }

    /**
     * 将碎片（或其所属的组）归位
     */
    placePiece(piece: PuzzlePiece): void {
        this.groupManager.place(piece);
        // 更新空间索引
        this.updateSpatialIndexForPieceOrGroup(piece);
    }

    /**
     * 取消碎片（或其所属的组）的归位状态
     */
    unplacePiece(piece: PuzzlePiece): void {
        this.groupManager.unplace(piece);
        
        // 触发事件
        const group = this.groupManager.getGroupByPiece(piece);
        if (group) {
            for (const p of group.getPieces()) {
                this.events.onPieceUnplaced?.(p);
            }
        } else {
            this.events.onPieceUnplaced?.(piece);
        }
    }

    /**
     * 获取归位到正确位置需要的偏移量
     */
    movePiece(piece: PuzzlePiece, delta: Vector2): void {
        if (piece.isPlaced) return;

        // 使用 GroupManager 移动（会自动处理组内所有碎片）
        this.groupManager.movePiece(piece, delta);

        // 注意：拖拽过程中不更新空间索引，只在释放时更新，以保证性能
    }

    /**
     * 旋转碎片（或其所属的组）
     */
    rotatePiece(piece: PuzzlePiece, clockwise: boolean = true): void {
        if (piece.isPlaced) return;

        // 使用 GroupManager 旋转（会自动处理组内所有碎片）
        this.groupManager.rotatePiece(piece, clockwise);

        // 旋转后更新空间索引，因为旋转可能改变碎片中心位置
        this.updateSpatialIndexForPieceOrGroup(piece);
    }

    /**
     * 释放碎片（拖拽结束时调用）
     * 返回吸附结果以及可能创建/合并的组信息
     */
    releasePiece(piece: PuzzlePiece): SnapResult & {
        group?: PieceGroup;
        isNewGroup?: boolean;
        mergedGroup?: PieceGroup;
    } {
        if (piece.isPlaced) {
            return { success: false };
        }

        const snapDistance = 12; // 归位触发容差（像素）

        // 优先检查是否可以归位到正确位置（整组归位）
        const snapDelta = this.groupManager.getSnapToCorrectDelta(piece, snapDistance);
        if (snapDelta) {
            // 移动到正确位置
            this.groupManager.movePiece(piece, snapDelta);
            // 执行归位
            this.groupManager.place(piece);

            // 更新空间索引
            this.updateSpatialIndexForPieceOrGroup(piece);

            // 触发事件
            const group = this.groupManager.getGroupByPiece(piece);
            if (group) {
                // 组归位
                for (const p of group.getPieces()) {
                    this.events.onPiecePlaced?.(p);
                }
            } else {
                // 单个碎片归位
                this.events.onPiecePlaced?.(piece);
            }

            // 检查是否可以与已放置的邻居合并
            this.checkForPlacedMerges(piece);

            this.checkGameComplete();

            return {
                success: true,
                snapPosition: piece.position,
                group: group || undefined
            };
        }

        // 检查是否可以吸附到相邻碎片
        // 计算动态搜索半径：基于碎片尺寸
        // 碎片尺寸约为 imageSize / (rings * 2 + 1)
        // 搜索半径应至少为碎片尺寸的 1.5 倍，以覆盖相邻碎片中心
        const pieceSize = this.config.imageSize / (this.config.rings * 2 + 1);
        const searchRadius = Math.max(pieceSize * 1.5, 200); // 至少 200

        const result = this.snapSystem.checkSnapToNeighbor(
            piece, 
            this.pieces, 
            this.groupManager, 
            this.spatialIndex,
            searchRadius
        );

        if (result.success && result.targetPiece) {
            console.log(`[PuzzleBoard] Before snap move:`, JSON.stringify({
                pieceId: piece.id,
                piecePos: piece.position,
                targetPieceId: result.targetPiece.id,
                targetPiecePos: result.targetPiece.position,
                snapPosition: result.snapPosition
            }));

            // 执行吸附位移
            const delta: Vector2 = {
                x: result.snapPosition!.x - piece.position.x,
                y: result.snapPosition!.y - piece.position.y
            };
            console.log(`[PuzzleBoard] Snap delta:`, JSON.stringify(delta));
            this.groupManager.movePiece(piece, delta);

            console.log(`[PuzzleBoard] After snap move:`, JSON.stringify({
                pieceId: piece.id,
                piecePos: piece.position,
                targetPieceId: result.targetPiece.id,
                targetPiecePos: result.targetPiece.position
            }));

            // 处理组合并
            const existingGroup1 = this.groupManager.getGroupByPiece(piece);
            const existingGroup2 = this.groupManager.getGroupByPiece(result.targetPiece);

            console.log(`[PuzzleBoard] Snap branch: group1=${existingGroup1?.id || 'none'}(${existingGroup1?.pieceCount || 0}pc), group2=${existingGroup2?.id || 'none'}(${existingGroup2?.pieceCount || 0}pc), matchedDir=${result.matchedDirection}`);

            let resultGroup: PieceGroup;
            let isNewGroup = false;
            let mergedGroup: PieceGroup | undefined;

            if (!existingGroup1 && !existingGroup2) {
                // 两个都是单独碎片，创建新组
                resultGroup = this.groupManager.handleSnap(piece, result.targetPiece);
                isNewGroup = true;
                if (result.matchedDirection !== undefined && result.targetPiece) {
                    this.events.onGroupCreated?.(resultGroup, piece, result.targetPiece, result.matchedDirection);
                }
            } else if (existingGroup1 && existingGroup2 && existingGroup1.id !== existingGroup2.id) {
                // 两个组合并
                mergedGroup = existingGroup1.pieceCount < existingGroup2.pieceCount ? existingGroup1 : existingGroup2;
                resultGroup = this.groupManager.handleSnap(piece, result.targetPiece);
                this.events.onGroupMerged?.(resultGroup, mergedGroup);
            } else {
                // 一个有组一个没有，添加到组
                resultGroup = this.groupManager.handleSnap(piece, result.targetPiece);
                if (result.matchedDirection !== undefined && result.targetPiece) {
                    this.events.onPieceJoinedGroup?.(piece, resultGroup, result.targetPiece, result.matchedDirection);
                }
            }

            // 更新空间索引
            this.updateSpatialIndexForPieceOrGroup(piece);

            // 触发吸附事件
            this.events.onPieceSnapped?.(piece, result);

            return {
                ...result,
                group: resultGroup,
                isNewGroup,
                mergedGroup
            };
        }

        // 吸附失败也要更新空间索引，因为碎片位置已经改变
        this.updateSpatialIndexForPieceOrGroup(piece);

        return { success: false };
    }

    /**
     * 检查是否可以与已放置的邻居合并
     */
    private checkForPlacedMerges(piece: PuzzlePiece): void {
        const group = this.groupManager.getGroupByPiece(piece);
        const piecesToCheck = group ? group.getPieces() : [piece];
        
        // 遍历当前组内的所有碎片，检查它们的邻居
        for (const p of piecesToCheck) {
            const neighbors = HexMath.getNeighbors(p.coord);
            for (const neighborCoord of neighbors) {
                const neighborPiece = this.getPieceByCoord(neighborCoord);
                
                // 必须存在，已放置，且不是同一个组
                if (neighborPiece && neighborPiece.isPlaced) {
                    const currentGroup = this.groupManager.getGroupByPiece(p);
                    const neighborGroup = this.groupManager.getGroupByPiece(neighborPiece);
                    
                    // 如果两个都在同一个组（且组存在），则跳过
                    const isSameGroup = currentGroup && neighborGroup && currentGroup.id === neighborGroup.id;

                    if (!isSameGroup) {
                        // 准备数据用于触发事件
                        const isCreation = !currentGroup && !neighborGroup;
                        const isMerge = !!(currentGroup && neighborGroup);
                        
                        // 执行合并
                        const resultGroup = this.groupManager.handleSnap(p, neighborPiece);
                        
                        // 确保新组也被标记为已放置
                        resultGroup.place();

                        const direction = HexMath.getSharedEdgeDirection(p.coord, neighborPiece.coord);

                        // 触发相应事件，以更新渲染器
                        if (isCreation) {
                            if (direction !== null) {
                                this.events.onGroupCreated?.(resultGroup, p, neighborPiece, direction);
                            }
                        } else if (isMerge) {
                            // 确定哪个组被合并了
                            const survivor = resultGroup;
                            const merged = (survivor.id === currentGroup!.id) ? neighborGroup! : currentGroup!;
                            this.events.onGroupMerged?.(survivor, merged);
                        } else {
                            // 单个碎片加入组
                            // 需要区分是谁加入了谁
                            let joiningPiece = p;
                            let targetPiece = neighborPiece;
                            let joinDir = direction; // p -> neighbor

                            if (currentGroup) {
                                // p 已经在组里，说明 neighborPiece（单个）加入了 p 的组
                                joiningPiece = neighborPiece;
                                targetPiece = p;
                                // 方向需要反转：joining -> target
                                if (joinDir !== null) joinDir = HexMath.getOppositeDirection(joinDir);
                            } else {
                                // p 是单个，加入了 neighborPiece 的组
                            }

                            if (joinDir !== null) {
                                this.events.onPieceJoinedGroup?.(joiningPiece, resultGroup, targetPiece, joinDir);
                            }
                        }
                    }
                }
            }
        }
    }

    /**
     * 检查游戏是否完成
     */
    private checkGameComplete(): void {
        const allPlaced = this.getAllPieces().every(p => p.isPlaced);

        if (allPlaced && !this._isComplete) {
            this._isComplete = true;
            this.events.onGameComplete?.();
        }
    }

    /**
     * 游戏是否完成
     */
    get isComplete(): boolean {
        return this._isComplete;
    }

    /**
     * 获取已放置的碎片数量
     */
    getPlacedCount(): number {
        return this.getAllPieces().filter(p => p.isPlaced).length;
    }

    /**
     * 获取总碎片数量
     */
    getTotalCount(): number {
        return this.pieces.size;
    }

    /**
     * 打乱碎片（使用智能布局）
     */
    shuffle(
        areaWidth: number, 
        areaHeight: number, 
        screenBounds?: { minX: number, maxX: number, minY: number, maxY: number }
    ): void {
        const pieces = this.getAllPieces();
        const slicedPiecesArray = Array.from(this.slicedPieces.values());
        const edgeGenerator = this.imageSlicer.getEdgeGenerator();

        // 初始化布局区域（使用完整的宽高）
        this.smartLayoutManager.initializeLayout(areaWidth, areaHeight);
        
        // 如果提供了屏幕边界，更新到布局管理器
        if (screenBounds) {
            this.smartLayoutManager.updateScreenBounds(
                screenBounds.minX, 
                screenBounds.maxX, 
                screenBounds.minY, 
                screenBounds.maxY
            );
        }

        // 执行智能布局
        this.smartLayoutManager.performLayout(pieces, slicedPiecesArray, edgeGenerator);

        // 清除所有碎片的组
        for (const piece of pieces) {
            if (!piece.isPlaced) {
                piece.setGroup(null);
            }
        }

        // 打乱后必须更新所有碎片的空间索引
        for (const piece of pieces) {
            this.spatialIndex.updatePiece(piece);
        }
    }

    /**
     * 传统打乱碎片（完全随机）
     */
    shuffleRandom(areaWidth: number, areaHeight: number): void {
        const pieces = this.getAllPieces();

        for (const piece of pieces) {
            if (piece.isPlaced) continue;

            // 随机位置
            const x = this.random.uniform(50, areaWidth - 50);
            const y = this.random.uniform(50, areaHeight - 50);
            piece.setPosition({ x, y });

            // 随机旋转 (0, 60, 120, 180, 240, 300)
            if (this.config.enableRotation) {
                const rotationSteps = this.random.int(0, 5);
                piece.setRotation(rotationSteps * 60);
            } else {
                piece.setRotation(0);
            }

            // 清除组
            piece.setGroup(null);
        }
    }

    /**
     * 整理碎片（归位到正确位置）
     */
    organize(): void {
        for (const piece of this.getAllPieces()) {
            piece.reset();
            piece.place();
        }

        this._isComplete = true;
        this.events.onGameComplete?.();
    }

    /**
     * 重置游戏
     */
    reset(): void {
        for (const piece of this.getAllPieces()) {
            piece.reset();
        }

        this._isComplete = false;
    }

    /**
     * 导出所有碎片状态
     */
    exportPieceStates(): PieceState[] {
        return this.getAllPieces().map(p => p.exportState());
    }

    /**
     * 导入碎片状态
     */
    importPieceStates(states: PieceState[]): void {
        for (const state of states) {
            const piece = this.pieces.get(state.id);
            if (piece) {
                piece.importState(state);
            }
        }

        // 检查游戏是否已完成
        this.checkGameComplete();
    }

    /**
     * 获取配置
     */
    getConfig(): GameConfig {
        return { ...this.config };
    }

    /**
     * 更新配置
     */
    updateConfig(config: Partial<GameConfig>): void {
        this.config = { ...this.config, ...config };
        
        // 核心修正：全链路同步配置
        this.imageSlicer.updateConfig(this.config);
        this.snapSystem.updateConfig({
            // 同步吸附感应距离等
        });
        
        // 如果 LayoutConfig 有变更，也需要同步给布局管理器
        this.smartLayoutManager.updateConfig({
            // 这里可以传入更新后的布局参数
        });
    }

    /**
     * 获取图片切割器
     */
    getImageSlicer(): ImageSlicer {
        return this.imageSlicer;
    }

    /**
     * 获取智能布局管理器
     */
    getSmartLayoutManager(): SmartLayoutManager {
        return this.smartLayoutManager;
    }

    /**
     * 获取与指定碎片同区域的碎片 ID 列表
     * 用于视觉退让功能
     */
    getSameRegionPieces(pieceId: string): string[] {
        return this.smartLayoutManager.getSameRegionPieces(pieceId);
    }

    /**
     * 获取与指定碎片同组的所有碎片
     * 用于拖拽时同步更新组内所有碎片
     */
    getGroupPieces(piece: PuzzlePiece): PuzzlePiece[] {
        return this.groupManager.getGroupPieces(piece);
    }

    /**
     * 获取碎片所属的组
     */
    getGroupByPiece(piece: PuzzlePiece): PieceGroup | null {
        return this.groupManager.getGroupByPiece(piece);
    }

    /**
     * 获取组管理器
     */
    getGroupManager(): GroupManager {
        return this.groupManager;
    }

    /**
     * 更新碎片或其所属组在空间索引中的位置
     */
    private updateSpatialIndexForPieceOrGroup(piece: PuzzlePiece): void {
        const group = this.groupManager.getGroupByPiece(piece);
        if (group) {
            for (const p of group.getPieces()) {
                this.spatialIndex.updatePiece(p);
            }
        } else {
            this.spatialIndex.updatePiece(piece);
        }
    }
}
